import type { ListMeta } from '@porotutu/react/paging'
import {
  currentPage,
  listPosition,
  listSummary,
  PAGE_SIZES,
  pageNumbers,
  paginated,
} from '@porotutu/react/paging'
import { useT } from '@/session/useSession'
import { NavLink } from '../NavLink/NavLink'
import styles from './Pagination.module.css'

export type PaginationProps = {
  meta: ListMeta
  offsetKey?: string
  sizes?: boolean
}

export function Pagination({ meta, offsetKey = 'offset', sizes = true }: PaginationProps) {
  const t = useT()

  if (meta.total === 0) return null

  const current = currentPage(meta)
  const at = (offset: number) => (prev: Record<string, string | number | undefined>) => ({
    ...prev,
    [offsetKey]: offset > 0 ? offset : undefined,
  })

  const keyed = pageNumbers(meta).map((number, index, all) => ({
    number,
    key: number === null ? `gap-after-${all[index - 1] ?? 'start'}` : String(number),
  }))

  return (
    <div className={styles.bar}>
      <p className={styles.summary}>
        {listSummary(meta, t)}
        {paginated(meta) ? <span className={styles.position}>{listPosition(meta, t)}</span> : null}
      </p>

      {paginated(meta) ? (
        <nav className={styles.pages} aria-label={t('pagination.label')}>
          <NavLink
            to="."
            disabled={meta.offset === 0}
            search={at(Math.max(0, meta.offset - meta.limit))}
            resetScroll={false}
          >
            {t('pagination.previous')}
          </NavLink>
          {keyed.map(({ number, key }) =>
            number === null ? (
              <span key={key} className={styles.gap} aria-hidden="true">
                &hellip;
              </span>
            ) : (
              <NavLink
                to="."
                key={key}
                current={number === current && 'page'}
                search={at((number - 1) * meta.limit)}
                resetScroll={false}
              >
                {String(number)}
              </NavLink>
            ),
          )}
          <NavLink
            to="."
            disabled={meta.offset + meta.limit >= meta.total}
            search={at(meta.offset + meta.limit)}
            resetScroll={false}
          >
            {t('pagination.next')}
          </NavLink>
        </nav>
      ) : null}

      {sizes && PAGE_SIZES[0] < meta.total ? (
        <nav className={styles.sizes} aria-label={t('pagination.sizes_label')}>
          <span className={styles.sizesLabel}>{t('pagination.per_page')}</span>
          {PAGE_SIZES.map((size) => (
            <NavLink
              to="."
              key={size}
              current={size === meta.limit}
              search={(prev) => ({ ...prev, limit: size, [offsetKey]: undefined })}
              resetScroll={false}
            >
              {String(size)}
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  )
}
